import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  FolderOpen, Send, Block, HelpOutline,
  Schedule, EmojiEvents, Cancel,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTenders } from '../../hooks/TenderContext';
import { STAT_CARDS } from '../../utils/constants';

const ICON_MAP = {
  FolderOpen: <FolderOpen />,
  Send: <Send sx={{ transform: 'scaleX(-1)' }} />,
  Block: <Block />,
  HelpOutline: <HelpOutline />,
  Schedule: <Schedule />,
  EmojiEvents: <EmojiEvents />,
  Cancel: <Cancel />,
};

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = React.useState(0);

  React.useEffect(() => {
    let start = 0;
    const duration = 800;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (value - start) * eased));
      
      if (progress < 1) requestAnimationFrame(animate);
    };
    
    animate();
  }, [value]);

  return <>{display}</>;
};

const StatCards = () => {
  const theme = useTheme();
  const { stats, setSelectedCategory } = useTenders();

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        md: 'repeat(4, 1fr)',
        lg: 'repeat(7, 1fr)',
      },
      gap: 2,
      mb: 3,
    }}>
      {STAT_CARDS.map((card, index) => (
        <motion.div
          key={card.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05, duration: 0.3 }}
        >
          <Card
            onClick={() => setSelectedCategory(card.key === 'total' ? 'all' : card.key)}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[3],
                borderColor: card.color,
              },
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: card.bgColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: card.color,
                }}>
                  {ICON_MAP[card.icon]}
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: card.color, mb: 0.5 }}>
                <AnimatedNumber value={stats[card.key] || 0} />
                {card.key === 'won' && stats.winRate > 0 && (
                  <Typography component="span" variant="body2" sx={{ color: card.color, mr: 1 }}>
                    ({stats.winRate}%)
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {card.label}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </Box>
  );
};

export default StatCards;
